import React, { useState, useEffect } from "react";
import { BaseModal } from "../ui/BaseModal.tsx";
import { CustomToggleTabs } from "../ui/CustomToggleTabs.tsx";
import { CustomAlert } from "../ui/CustomAlert.tsx";
import { useTheme } from "../ThemeContext";
import { Plus, Users } from "lucide-react";
import AssociationViewTab from "./association/AssociationViewTab.tsx";
import AssociationCreateTab from "./association/AssociationCreateTab.tsx";
import { useAssociationData } from "../../hooks/useAssociationData.ts";
import { useAssociationActions } from "../../hooks/useAssociationActions.ts";
import { useAlertManager } from "../../hooks/useAlertManager.ts";
import { AssociationModalProps } from "../ui/types.ts";

const AssociationModal: React.FC<AssociationModalProps> = ({ onClose, isOpen }) => {
  const { isDarkMode } = useTheme();
  const [selectedTab, setSelectedTab] = useState("view");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  const [textConfig, setTextConfig] = useState<any>({});

  const fetchTextConfig = async () => {
    try {
      const response = await fetch("/text.json");
      const data = await response.json();
      setTextConfig(data);
    } catch (error) {
    }
  };

  useEffect(() => {
    fetchTextConfig();
  }, []);

  const {
    associations,
    users,
    profiles,
    isLoading,
    pagination,
    filters,
    totalPages,
    totalAssociations,
    searchUserId,
    handlePageChange,
    handleFilterChange,
    clearFilters,
    handleSearchByUser,
    fetchAssociations,
    fetchInitialData
  } = useAssociationData(isOpen);

  const {
    selectedUsers,
    selectedProfiles,
    handleUserSelect,
    handleProfileSelect,
    handleClearSelections,
    handleCreateAssociation,
    handleDeleteAssociation,
    hasExistingAssociation,
    isUserAlreadyAssociated,
    getAssociatedProfile
  } = useAssociationActions(associations, fetchAssociations);

  const { alertConfig, setAlertConfig } = useAlertManager();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getTextColorClass = () =>
    isDarkMode ? "text-gray-300" : "text-gray-600";

  const tabOptions = [
    {
      value: "view",
      label: textConfig.ASSOCIATION_MODAL_TAB_VIEW || "Asociaciones existentes",
      icon: <Users size={18} />,
    },
    {
      value: "create",
      label: textConfig.ASSOCIATION_MODAL_TAB_CREATE || "Crear asociación",
      icon: <Plus size={18} />,
    },
  ];

  const viewTabProps = {
    associations,
    isLoading,
    isMobile,
    isDarkMode,
    pagination,
    filters,
    profiles,
    totalPages,
    totalAssociations,
    searchUserId,
    onDeleteAssociation: (userId: string) => 
      handleDeleteAssociation(userId, setAlertConfig),
    onPageChange: handlePageChange,
    onFilterChange: handleFilterChange,
    onClearFilters: clearFilters,
    onSearchByUser: handleSearchByUser
  };

  const createTabProps = {
    users,
    profiles,
    selectedUsers,
    selectedProfiles,
    isMobile,
    isDarkMode,
    hasExistingAssociation: hasExistingAssociation(),
    isUserAlreadyAssociated,
    getAssociatedProfile,
    onUserSelect: handleUserSelect,
    onProfileSelect: handleProfileSelect,
    onClearSelections: handleClearSelections,
    onCreateAssociation: () => 
      handleCreateAssociation(setAlertConfig)
  };

  return (
    <>
      <BaseModal title={textConfig.ASSOCIATION_MODAL_TITLE} onClose={onClose} isOpen={isOpen}>
        <div className="w-full max-w-2xl">
          <p className={`mb-4 ${getTextColorClass()}`}>
            {textConfig.ASSOCIATION_MODAL_DESCRIPTION}
          </p>

          <div className="mb-4">
            <CustomToggleTabs
              value={selectedTab}
              onChange={setSelectedTab}
              options={tabOptions}
            />
          </div>

          {selectedTab === "view" ? (
            <AssociationViewTab {...viewTabProps} />
          ) : (
            <AssociationCreateTab {...createTabProps} />
          )}
        </div>
      </BaseModal>
      
      <CustomAlert
        open={alertConfig.open}
        type={alertConfig.type}
        message={alertConfig.message}
        onClose={() => setAlertConfig((prev) => ({ ...prev, open: false }))}
        textConfig={textConfig}
        title={alertConfig.title}
        onConfirm={alertConfig.onConfirm}
        $isDark={isDarkMode}
      />
    </>
  );
};

export default AssociationModal;